import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, X } from "lucide-react";

import type {
  AdminAction,
  AdminUser,
  AdminUserConcept,
  AdminUserDetail,
} from "@/hooks/useAdminUsers";
import {
  useUserAudit,
  useUserConcepts,
  useUserDetail,
} from "@/hooks/useAdminUsers";

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function Avatar({
  user,
  size = 32,
}: {
  user: AdminUser;
  size?: number;
}) {
  const initials = [user.first_name, user.last_name]
    .filter(Boolean)
    .map((n) => n![0].toUpperCase())
    .join("");
  const fallback = initials || (user.email?.[0]?.toUpperCase() ?? "?");

  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt=""
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className="rounded-full bg-muted flex items-center justify-center text-muted-foreground font-medium shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {fallback}
    </div>
  );
}

export { Avatar, relativeTime };

interface UserDetailDrawerProps {
  user: AdminUser | null;
  open: boolean;
  onClose: () => void;
}

type TabId = "profile" | "subscription" | "concepts" | "audit";

export default function UserDetailDrawer({
  user,
  open,
  onClose,
}: UserDetailDrawerProps) {
  const [tab, setTab] = useState<TabId>("profile");
  const [refreshStripe, setRefreshStripe] = useState(false);

  // Always call hooks — disabled at the query level when id is missing.
  const userId = user?.id ?? null;
  const detailQuery = useUserDetail(userId, refreshStripe);

  // Reset local state when the selected user changes or drawer closes.
  useEffect(() => {
    setTab("profile");
    setRefreshStripe(false);
  }, [userId]);

  if (!open || !user) return null;

  // Prefer server detail (fresher, includes usage + stripe) but fall back to
  // the row-level user prop until it arrives so the drawer doesn't flash empty.
  const detail: AdminUserDetail | null = detailQuery.data ?? null;

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
    user.display_name ||
    user.email;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[28rem] max-w-full bg-background border-l shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <span className="font-semibold text-sm text-muted-foreground">
            User Detail
          </span>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted transition-colors"
            aria-label="Close drawer"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Identity */}
        <div className="p-4 border-b flex items-center gap-3">
          <Avatar user={user} size={48} />
          <div className="min-w-0">
            <div className="font-semibold truncate">{displayName}</div>
            <div className="text-sm text-muted-foreground truncate">
              {user.email}
            </div>
            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium capitalize">
              {user.plan}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label="User sections"
          className="flex border-b text-sm"
        >
          {(
            [
              { id: "profile", label: "Profile" },
              { id: "subscription", label: "Subscription" },
              { id: "concepts", label: "Concepts" },
              { id: "audit", label: "Audit" },
            ] as const
          ).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setTab(t.id)}
                className={
                  "flex-1 px-3 py-2 font-medium transition-colors " +
                  (active
                    ? "border-b-2 border-primary text-foreground"
                    : "text-muted-foreground hover:text-foreground")
                }
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {tab === "profile" && (
            <ProfileTab user={user} detail={detail} />
          )}
          {tab === "subscription" && (
            <SubscriptionTab
              detail={detail}
              refreshStripe={refreshStripe}
              isFetching={detailQuery.isFetching}
              onRefresh={() => setRefreshStripe(true)}
            />
          )}
          {tab === "concepts" && userId && <ConceptsTab userId={userId} />}
          {tab === "audit" && userId && <AuditTab userId={userId} />}
        </div>
      </div>
    </>
  );
}

function ProfileTab({
  user,
  detail,
}: {
  user: AdminUser;
  detail: AdminUserDetail | null;
}) {
  const effective = detail ?? user;
  const tags = effective.tags ?? [];
  const planNotes = effective.plan_notes;

  return (
    <div className="p-4 space-y-4 text-sm">
      <Field label="Auth ID">
        <span className="font-mono text-xs break-all">{user.auth_id}</span>
      </Field>

      <Field label="Tags">
        {tags.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {tags.map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </Field>

      <Field label="Plan Notes">
        <div className="rounded border bg-muted/30 p-2 text-xs whitespace-pre-wrap min-h-[2rem]">
          {planNotes && planNotes.trim().length > 0
            ? planNotes
            : "No notes."}
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <Field label="Created">
          <span>{new Date(user.created_at).toLocaleDateString()}</span>
        </Field>
        <Field label="Last Active">
          <span>{relativeTime(user.last_active_at)}</span>
        </Field>
        {effective.deactivated_at && (
          <Field label="Deactivated">
            <span>
              {new Date(effective.deactivated_at).toLocaleDateString()}
            </span>
          </Field>
        )}
        {effective.deleted_at && (
          <Field label="Deleted">
            <span>
              {new Date(effective.deleted_at).toLocaleDateString()}
            </span>
          </Field>
        )}
        <Field label="Concepts">
          <span>{effective.concept_count}</span>
        </Field>
      </div>
    </div>
  );
}

function SubscriptionTab({
  detail,
  refreshStripe,
  isFetching,
  onRefresh,
}: {
  detail: AdminUserDetail | null;
  refreshStripe: boolean;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  const status = detail?.stripe_subscription_status ?? "none";
  const usage = detail?.usage_cost_cents_total ?? 0;

  const statusClasses =
    status === "active"
      ? "bg-green-100 text-green-800"
      : status === "canceled"
        ? "bg-amber-100 text-amber-800"
        : "bg-gray-100 text-gray-700";

  return (
    <div className="p-4 space-y-4 text-sm">
      <Field label="Stripe Status">
        <div className="flex items-center gap-2">
          <span
            className={
              "inline-block text-xs px-2 py-0.5 rounded-full font-medium capitalize " +
              statusClasses
            }
          >
            {status}
          </span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isFetching}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border hover:bg-muted disabled:opacity-50"
          >
            {isFetching && refreshStripe ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" />
            )}
            Refresh from Stripe
          </button>
        </div>
      </Field>

      <Field label="Usage (all-time)">
        <span className="font-medium">
          ${(usage / 100).toFixed(2)}
        </span>
      </Field>
    </div>
  );
}

function ConceptsTab({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useUserConcepts(userId);

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Loading concepts…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-4 text-sm text-red-600">
        Failed to load concepts.
      </div>
    );
  }
  const rows = data ?? [];
  if (rows.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        This user has no concepts.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2 text-sm">
      <ul className="space-y-2">
        {rows.map((c) => (
          <ConceptRow key={c.id} concept={c} />
        ))}
      </ul>
    </div>
  );
}

function ConceptRow({ concept }: { concept: AdminUserConcept }) {
  const failing = concept.monitor_consecutive_failures > 0;
  const durSec = concept.last_monitor_duration_seconds;
  const durLabel =
    durSec !== null
      ? durSec >= 60
        ? `${Math.round(durSec / 6) / 10}min`
        : `${Math.round(durSec)}s`
      : null;

  // "0 new in last run" chip only when the last run actually succeeded —
  // skips and failures are not barren signals.
  const barren =
    concept.last_monitor_status === "success" &&
    concept.last_monitor_new_papers === 0;

  return (
    <li className="rounded border p-2 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium truncate" title={concept.name}>
          {concept.name}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {failing && (
            <span
              className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-medium"
              title={
                concept.last_monitor_error
                  ? `${concept.last_monitor_error}${
                      concept.last_monitor_error_at
                        ? ` (${relativeTime(concept.last_monitor_error_at)})`
                        : ""
                    }`
                  : "Monitor failing"
              }
            >
              ⚠ {concept.monitor_consecutive_failures} fail
              {concept.monitor_consecutive_failures === 1 ? "" : "s"}
            </span>
          )}
          {barren && !failing && (
            <span
              className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium"
              title="Last run found 0 new papers — seeds may be stale"
            >
              0 new
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            {concept.paper_count} paper{concept.paper_count === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-3">
        <span>created {new Date(concept.created_at).toLocaleDateString()}</span>
        <span>
          monitor {concept.monitor_enabled ? "on" : "off"}
          {concept.last_monitor_run
            ? ` · last ${relativeTime(concept.last_monitor_run)}`
            : concept.monitor_enabled
              ? " · never run"
              : ""}
          {durLabel ? ` · ${durLabel}` : ""}
        </span>
      </div>
    </li>
  );
}

function AuditTab({ userId }: { userId: string }) {
  const [beforeId, setBeforeId] = useState<number | undefined>(undefined);
  const [accumulated, setAccumulated] = useState<AdminAction[]>([]);
  const [seenKeys, setSeenKeys] = useState<string>(userId);

  // Reset accumulation whenever the user changes.
  useEffect(() => {
    if (seenKeys !== userId) {
      setAccumulated([]);
      setBeforeId(undefined);
      setSeenKeys(userId);
    }
  }, [userId, seenKeys]);

  const { data, isLoading, isFetching } = useUserAudit(userId, beforeId);

  // Merge each new page into the accumulated list (dedupe by id).
  useEffect(() => {
    if (!data) return;
    setAccumulated((prev) => {
      const byId = new Map<number, AdminAction>();
      for (const row of prev) byId.set(row.id, row);
      for (const row of data) byId.set(row.id, row);
      return Array.from(byId.values()).sort((a, b) => b.id - a.id);
    });
  }, [data]);

  const rows = accumulated;
  const lastId = rows.length > 0 ? rows[rows.length - 1].id : undefined;
  const canLoadMore =
    lastId !== undefined && (data?.length ?? 0) > 0 && !isFetching;

  if (isLoading && rows.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Loading audit log…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No admin actions recorded for this user.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 text-sm">
      <ul className="space-y-2">
        {rows.map((row) => (
          <AuditRow key={row.id} row={row} />
        ))}
      </ul>
      <div className="pt-2">
        <button
          type="button"
          disabled={!canLoadMore}
          onClick={() => {
            if (lastId !== undefined) setBeforeId(lastId);
          }}
          className="w-full text-xs px-2 py-1.5 rounded border hover:bg-muted disabled:opacity-50"
        >
          {isFetching ? "Loading…" : "Load older"}
        </button>
      </div>
    </div>
  );
}

function AuditRow({ row }: { row: AdminAction }) {
  const meta = useMemo(() => {
    const keys = Object.keys(row.metadata ?? {});
    return keys.map((k) => ({
      key: k,
      value: formatMetadataValue((row.metadata as Record<string, unknown>)[k]),
    }));
  }, [row.metadata]);

  return (
    <li className="rounded border p-2 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
          {row.action}
        </span>
        <span
          className="text-xs text-muted-foreground"
          title={row.created_at}
        >
          {relativeTime(row.created_at)}
        </span>
      </div>
      <div className="text-[10px] text-muted-foreground font-mono flex flex-wrap gap-x-3">
        <span>admin {truncateId(row.admin_user_id)}</span>
        {row.target_user_id && (
          <span>target {truncateId(row.target_user_id)}</span>
        )}
      </div>
      {meta.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-0.5 text-xs">
          {meta.map(({ key, value }) => (
            <div key={key} className="contents">
              <dt className="text-muted-foreground">{key}</dt>
              <dd className="truncate">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}

function formatMetadataValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function truncateId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}
